import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { CookieBanner } from "@/components/CookieConsent/CookieBanner";
import { CookiePreferences } from "@/components/CookieConsent/CookiePreferences";
import { CookieConsentProvider, useCookieConsentContext } from "@/contexts/CookieConsentContext";
import Index from "./pages/Index";
import ThankYou from "./pages/ThankYou";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminLeads from "./pages/admin/Leads";
import AdminReviews from "./pages/admin/Reviews";
import AdminGallery from "./pages/admin/Gallery";
import AdminFAQs from "./pages/admin/FAQs";
import AdminSEO from "./pages/admin/SEO";
import AdminSettings from "./pages/admin/Settings";
import ProtectedRoute from "./components/ProtectedRoute";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const LegacyRedirect = () => {
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();
  return <Navigate to={queryString ? `/?${queryString}` : "/"} replace />;
};

const AppContent = () => {
  const {
    showBanner,
    showPreferences,
    preferences,
    acceptAll,
    rejectAll,
    openPreferences,
    closePreferences,
    updatePreferences,
  } = useCookieConsentContext();

  return (
    <>
      <Toaster />
      <Sonner />
      {showBanner && (
        <CookieBanner
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onManagePreferences={openPreferences}
        />
      )}
      <CookiePreferences
        isOpen={showPreferences}
        onClose={closePreferences}
        onSave={updatePreferences}
        currentPreferences={preferences}
      />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/index.html" element={<LegacyRedirect />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="gallery" element={<AdminGallery />} />
            <Route path="faqs" element={<AdminFAQs />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <CookieConsentProvider>
          <AppContent />
        </CookieConsentProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
